class HeroBlogPost {
  constructor(element) {
    this.element = element;
    this.currentIndex = 0;
    this.isDesktop = window.innerWidth >= 1024;
    this.autoplayTimer = null;
    this.totalSlides = this.element.querySelectorAll("[data-article-index]").length;

    // Get autoplay settings from data attributes
    this.autoplayEnabled = this.element.getAttribute("data-autoplay") === "true";
    this.autoplayInterval = parseInt(this.element.getAttribute("data-autoplay-interval")) || 5000;

    this.init();
    this.bindEvents();

    // Start autoplay if enabled
    if (this.autoplayEnabled) {
      this.startAutoplay();
    }
  }

  init() {
    // Initialize desktop swiper for content
    const contentSwiper = this.element.querySelector("[data-content-swiper]");
    if (contentSwiper) {
      this.contentSwiperEl = contentSwiper;
      this.contentSwiperReady = false;

      this.initializeSwiper(contentSwiper, {
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        speed: 600,
        loop: true,
        allowTouchMove: false,
        watchSlidesProgress: true,
      });

      // Mark content swiper as ready when initialized
      contentSwiper.addEventListener('swiperinit', () => {
        this.contentSwiperReady = true;
      });

      // Check if already initialized
      if (contentSwiper.swiper) {
        this.contentSwiperReady = true;
      }
    }

    // Initialize mobile swiper
    const mobileSwiper = this.element.querySelector(
      "[data-mobile-hero-swiper]"
    );
    if (mobileSwiper) {
      this.mobileSwiperEl = mobileSwiper;
      this.initializeSwiper(mobileSwiper, {
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        speed: 600,
        loop: true,
        pagination: {
          clickable: true,
          dynamicBullets: false,
        },
        watchSlidesProgress: true,
      });

      // Listen for swiper initialization
      mobileSwiper.addEventListener('swiperinit', () => {
        this.setupMobileSwiperEvents(mobileSwiper);
      });

      // If already initialized, set up events immediately
      if (mobileSwiper.swiper) {
        this.setupMobileSwiperEvents(mobileSwiper);
      }
    }
  }

  setupMobileSwiperEvents(mobileSwiper) {
    if (!mobileSwiper || !mobileSwiper.swiper) return;

    // Use the swiper API's slideChange event
    mobileSwiper.swiper.on('slideChange', () => {
      const newIndex = mobileSwiper.swiper.activeIndex;
      this.currentIndex = newIndex;
      this.syncContentSwiper(newIndex);

      // Restart autoplay after user swipes
      if (this.autoplayEnabled) {
        this.restartAutoplay();
      }
    });

    // Also listen for the native custom event as backup
    mobileSwiper.addEventListener('swiperslidechange', (e) => {
      if (e.detail && e.detail[0]) {
        const newIndex = e.detail[0].activeIndex;
        this.currentIndex = newIndex;
        this.syncContentSwiper(newIndex);

        // Restart autoplay after user swipes
        if (this.autoplayEnabled) {
          this.restartAutoplay();
        }
      }
    });
  }

  initializeSwiper(container, options) {
    const swiperParams = Object.assign({}, options);

    // Assign params to the element
    Object.assign(container, swiperParams);

    // Initialize
    container.initialize();

    return container;
  }

  bindEvents() {
    // Desktop: Title click events
    const titleButtons = this.element.querySelectorAll("[data-article-index]");
    titleButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        const index = parseInt(button.getAttribute("data-article-index"));
        this.changeSlide(index);
        // Restart autoplay after user interaction
        if (this.autoplayEnabled) {
          this.restartAutoplay();
        }
      });
    });

    // Handle resize
    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const wasDesktop = this.isDesktop;
        this.isDesktop = window.innerWidth >= 1024;

        // Reinitialize if switching between mobile and desktop
        if (wasDesktop !== this.isDesktop) {
          this.changeSlide(0);
        }
      }, 250);
    });

    // Pause autoplay on hover
    if (this.autoplayEnabled) {
      this.element.addEventListener("mouseenter", () => {
        this.stopAutoplay();
      });

      this.element.addEventListener("mouseleave", () => {
        this.startAutoplay();
      });
    }
  }

  changeSlide(index) {
    if (index === this.currentIndex) return;

    this.currentIndex = index;

    // Update desktop images
    const images = this.element.querySelectorAll("[data-image-index]");
    images.forEach((img, i) => {
      if (i === index) {
        img.classList.add("active");
      } else {
        img.classList.remove("active");
      }
    });

    // Update desktop title buttons
    const titleButtons = this.element.querySelectorAll("[data-article-index]");
    titleButtons.forEach((button, i) => {
      if (i === index) {
        button.classList.add("active");
      } else {
        button.classList.remove("active");
      }
    });

    // Sync content swiper
    this.syncContentSwiper(index);
  }

  syncContentSwiper(index) {
    const contentSwiper = this.element.querySelector("[data-content-swiper]");
    if (!contentSwiper) return;

    const trySync = () => {
      if (contentSwiper.swiper && this.contentSwiperReady) {
        contentSwiper.swiper.slideTo(index, 600);
        return true;
      }
      return false;
    };

    // Try immediately
    if (!trySync()) {
      // If not ready, wait and retry
      let attempts = 0;
      const maxAttempts = 10;
      const retry = setInterval(() => {
        attempts++;
        if (trySync() || attempts >= maxAttempts) {
          clearInterval(retry);
        }
      }, 100);
    }
  }

  startAutoplay() {
    this.stopAutoplay(); // Clear any existing timer
    this.autoplayTimer = setInterval(() => {
      this.nextSlide();
    }, this.autoplayInterval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  nextSlide() {
    // Calculate next index with looping
    const nextIndex = (this.currentIndex + 1) % this.totalSlides;

    if (this.isDesktop) {
      // Desktop: use manual change
      this.changeSlide(nextIndex);
    } else {
      // Mobile: use swiper navigation
      const mobileSwiper = this.element.querySelector("[data-mobile-hero-swiper]");
      if (mobileSwiper && mobileSwiper.swiper) {
        mobileSwiper.swiper.slideNext();
      }
    }
  }
}

// Initialize all hero blog post sections
document.addEventListener("DOMContentLoaded", () => {
  const heroBlogPosts = document.querySelectorAll("[data-hero-blog-post]");
  heroBlogPosts.forEach((element) => {
    new HeroBlogPost(element);
  });
});

// Re-initialize on Shopify section:load event (for theme editor)
if (Shopify && Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    const heroBlogPost = event.target.querySelector("[data-hero-blog-post]");
    if (heroBlogPost) {
      new HeroBlogPost(heroBlogPost);
    }
  });
}
